/**
 * Tab panel widget
 * @url https://www.w3.org/TR/2013/WD-wai-aria-practices-20130307/#tabpanel
 * @url http://oaa-accessibility.org/example/34/
 */

// Imports
import KeyCodes from '../KeyCodes';
import GlobalVars from '../GlobalVars';
import TabManager from './TabManager';
import {
  triggerTablistTabChangeEvent,
} from '../Events';

// Constants (selectors)
const tabList = '[data-js="tablist"]';
const tabItem = '[role="tab"]';
const contentPanel = '[role="tabpanel"]';

// Constants (classes)
const activeClass = 'active';

// Tab intance list
const instancesArray = [];

class Tablist {
  constructor(aoContainer, aoOptions) {
    this.$container = $(aoContainer);
    this.$tabs = this.$container.find(tabItem);

    this.options = {
      change: null,
      handleContentDisplay: true,
    };
    Object.assign(this.options, aoOptions);

    this.tabManager = (!this.$tabs.length) ? null : new TabManager(this.$tabs, this.options);
  }
  /**
   * Activates a tab.
   * @param {jQuery}  aoTab         - Tab element.
   * @param {Boolean} [abNoTrigger] - Set to true for not triggering "tab change" event.
   * @return {Object} Plain object:
   *            toTab (jQuery)
   *            toContent (jQuery)
   *            fromtab (jQuery)
   *            fromContent (jQuery)
   * Else returns false if error.
   */
  setActive(aoTab, abNoTrigger) {
    const $tab = $(aoTab);
    const $target = $(`#${$tab.attr('aria-controls')}`);
    const $toContent = $target.filter(contentPanel);

    // content found
    // already active ?
    if ($toContent.length && $toContent.hasClass(activeClass)) {
      $toContent.attr('aria-hidden', false);

      return false;
    }

    // get last active tab + content
    const $lastTab = this.$tabs.filter(`.${activeClass}`);
    let $lastContent = $toContent.siblings(`${contentPanel}.${activeClass}`);

    if (!$lastContent.length && $lastTab.length) {
      // Try to get content
      // from last active tab

      const $lastTabTarget = $(`#${$lastTab.attr('aria-controls')}`);
      if ($lastTabTarget.length) {
        $lastContent = $lastTabTarget.filter(`${contentPanel}.${activeClass}`);
      }
    }

    this.tabManager.setSelected($tab, true);

    if ($lastTab.length && $lastContent.length) {
      // hide last content
      this.tabManager.hideContent($lastContent);
    }

    // show new content
    this.tabManager.showContent($toContent);

    if (!abNoTrigger && OCMS.productpage) {
      // Trigger "tab change" event
      const tabName = $.trim($tab.text());
      const productName = OCMS.productpage.pageArticleTitle !== undefined ?
                          OCMS.productpage.pageArticleTitle : '';
      const productId = OCMS.productpage.sku !== undefined ? OCMS.productpage.sku
                        : '';

      triggerTablistTabChangeEvent({
        productname: productName,
        tabname: `${tabName.toUpperCase()} tab`,
        productid: productId,
      });
    }

    return {
      toTab: $tab,
      toContent: $toContent,
      fromTab: $lastTab.length ? $lastTab : undefined,
      fromContent: $lastContent.length ? $lastContent : undefined,
    };
  }

  /**
   * Focus handler for a tab.
   * @param {Event} aoEvent - Event object.
   */
  focusTabHandler(aoEvent) {
    const $tab = $(aoEvent.currentTarget);
    const ui = this.setActive($tab);

    if (typeof this.options.change === 'function') {
      this.options.change.call(this, aoEvent, ui);
    }
  }

  /**
   * Click handler for a tab.
   * @param {Event} aoEvent - Event object.
   * @return {Boolean} Returns false when preventing default.
   */
  clickTabHandler(aoEvent) {
    const $tab = $(aoEvent.currentTarget);

    if ($tab.length && !$tab.is('a')) {
      if (!$tab.is(`.${activeClass}`)) {
        const ui = this.setActive($tab);

        if (typeof this.options.change === 'function') {
          this.options.change.call(this, aoEvent, ui);
        }
      }

      aoEvent.preventDefault();

      return false;
    }

    return true;
  }

  /**
   * Keydown handler for a tab.
   * @param {Event} aoEvent - Event object.
   * @return {Boolean} Returns false when preventing default.
   */
  keydownTabHandler(aoEvent) {
    const $tab = $(aoEvent.currentTarget);

    if (aoEvent.altKey) {
      // do nothing
      return true;
    }

    let $targetTab = {};

    switch (aoEvent.keyCode) {
      case KeyCodes.enter:
      case KeyCodes.space: {
        if ($tab.is('a')) {
          return true;
        }

        aoEvent.stopPropagation();

        return false;
      }
      case KeyCodes.left: {
        if (GlobalVars.SiteDirection === 'rtl') {
          // Next tab
          $targetTab = this.tabManager.getNextTab($tab);
        } else {
          // Previous tab
          $targetTab = this.tabManager.getPreviousTab($tab);
        }

        break;
      }
      case KeyCodes.right: {
        if (GlobalVars.SiteDirection === 'rtl') {
          // Previous tab
          $targetTab = this.tabManager.getPreviousTab($tab);
        } else {
          // Next tab
          $targetTab = this.tabManager.getNextTab($tab);
        }

        break;
      }
      case KeyCodes.up: {
        $targetTab = this.tabManager.getPreviousTab($tab);

        break;
      }
      case KeyCodes.down: {
        $targetTab = this.tabManager.getNextTab($tab);

        break;
      }
      case KeyCodes.home: {
        $targetTab = this.$tabs.first();

        break;
      }
      case KeyCodes.end: {
        $targetTab = this.$tabs.last();

        break;
      }
      default: {
        break;
      }
    }

    if ($targetTab.length) {
      $targetTab.focus();

      aoEvent.stopPropagation();

      return false;
    }

    return true;
  }

  initAriaTabContent(aoElement) {
    const $tab = $(aoElement);
    const $target = $(`#${$tab.attr('aria-controls')}`);

    this.tabManager.hideContent($target, true);
  }

  initAria() {
    this.$tabs
      .attr({
        'aria-selected': false,
        tabindex: -1,
      })
      .first().attr('tabindex', 0)
      .end()
      .each((anIndex, aoElement) => {
        this.initAriaTabContent(aoElement);
      });
  }

  /**
   * Activates a default tab at init.
   */
  initActive() {
    const $activeTab = this.$tabs.filter(`.${activeClass}`);

    if ($activeTab.length) {
      this.setActive($activeTab, true);
    }
  }

  /**
   * Resets the tablist + tabs to their initial state.
   */
  reset() {
    this.initAria();
    this.$tabs
      .removeClass(activeClass)
      .show();
  }

  /**
   * Bind events
   */
  initBinds() {
    this.$tabs
      .on('focus', (aoEvent) => {
        this.focusTabHandler(aoEvent);
      })
      .on('click', (aoEvent) => {
        this.clickTabHandler(aoEvent);
      })
      .keydown((aoEvent) => {
        this.keydownTabHandler(aoEvent);
      });
  }

  /**
   * Init
   */
  init() {
    if (!this.$tabs.length) {
      return;
    }

    this.initAria();
    this.initActive();
    this.initBinds();

    this.$container.trigger('loaded.tab');
  }
}

/**
 * Init
 */
function init() {
  $(tabList).each(function eachInit() {
    const tablistInstance = new Tablist(this);
    tablistInstance.init();
  });
}

/**
 * Exposed getter
 */
function getAllTabs() {
  return instancesArray;
}

/**
 * Create feature on element.
 * @param  {jQuery|string} aoContainer - Container of tabs + tab contents.
 * @param  {Object} aoOptions          - A set of key/value pairs:
 *    change (function): callback with event, ui arguments.
 *        ui properties are:
 *            toTab (jQuery)
 *            toContent (jQuery)
 *            fromtab (jQuery)
 *            fromContent (jQuery)
 */
function create(aoContainer, aoOptions) {
  const $container = $(aoContainer);
  // const instancesArray = [];

  $container.each(function eachInit() {
    const tablistInstance = new Tablist(this, aoOptions);
    instancesArray.push(tablistInstance);

    tablistInstance.init();
  });

  if (!instancesArray.length) {
    return null;
  }

  if (instancesArray.length === 1) {
    return instancesArray[0];
  }

  return instancesArray;
}

export {
  init,
  create,
  getAllTabs,
};



// WEBPACK FOOTER //
// ./js/common/widgets/Tablist.js